import { DebouncedFunc } from 'lodash';
import _debounce from 'lodash/debounce';
import { useEffect, useMemo, useRef, useState } from 'react';

export type UseDebouncedValueArgs<TValue> = {
    /**
     * The value with updates debounced by the hook
     */
    debouncedValue: TValue;
    /**
     * The onChange handler that's invocations will be debounced
     */
    debouncedOnChange: (value: TValue) => void;
    debounceInterval: number;
    /**
     * If true, the debouncing will be disabled and the debouncedValue will be the same as the onChange value
     */
    isDisabled?: boolean;
};

export type UseDebouncedValueReturnValue<TValue> = {
    /**
     * The value that reflects updates immediately
     */
    value: TValue;
    /**
     * The onChange that can be called repeatedly.  The parent will only receive debounced updates
     */
    onChange: (value: TValue, invokeImmediately?: boolean) => void;
};

export const useDebouncedValue = <TValue>({
    debouncedValue,
    debouncedOnChange,
    debounceInterval,
    isDisabled = false,
}: UseDebouncedValueArgs<TValue>): UseDebouncedValueReturnValue<TValue> => {
    const oldDebounceAppliedOnChange = useRef<DebouncedFunc<(value: TValue) => void> | null>(null);
    const [value, onChange] = useState(debouncedValue);
    const parentChangeRef = useRef<(value: TValue) => void>(debouncedOnChange);

    const debounceAppliedOnChange = useMemo(() => {
        if (oldDebounceAppliedOnChange.current) {
            oldDebounceAppliedOnChange?.current?.cancel();
        }
        return _debounce((value: TValue) => {
            parentChangeRef.current(value);
        }, debounceInterval);
    }, [debounceInterval]);
    oldDebounceAppliedOnChange.current = debounceAppliedOnChange;

    // cleanup any pending debounced calls on unmount
    useEffect(
        () => () => {
            oldDebounceAppliedOnChange.current?.cancel();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    // anytime the parent changes the value, propagate it despite the internal state
    useEffect(() => {
        onChange(value);
        debounceAppliedOnChange?.cancel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedValue]);

    return {
        value: isDisabled ? debouncedValue : value,
        // @ts-expect-error TS2322
        onChange: isDisabled
            ? debouncedOnChange
            : (value: TValue, invokeImmediately: boolean) => {
                  // reflect the internal value immediately
                  onChange(value);

                  if (invokeImmediately) {
                      debounceAppliedOnChange?.cancel();
                      parentChangeRef.current(value);
                  } else {
                      // debounce the parent's onChange
                      debounceAppliedOnChange(value);
                  }
              },
    };
};
